// hooks/useAuthSync.js
/**
 * useAuthSync - Syncs Clerk authentication with our backend
 *
 * Responsibilities:
 * 1. Wait for Clerk webhook to create user in database
 * 2. Fetch user data from backend
 * 3. Store user in Redux
 * 4. Load user settings for complete profiles
 *
 * Auth status is DERIVED from user data via useAuthStatus, not set here.
 */
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { authenticateUser, registerUser } from "../api/authenticationAPI";
import { setCurrentUser } from "../redux/actions/userActions";
import { getUserSettings, markOnboardingComplete } from "../api/usersAPI";
import {
  saveUserSettings,
  setDefaultSettings,
} from "../redux/actions/userSettingsActions";
import { isProfileComplete } from "../utils/profileValidation";

// Retry helper with exponential backoff
// Waits for Clerk webhook to create user in database before giving up
// 404 = user not created yet by webhook, keep retrying
// Total wait time: ~8 seconds (1s + 1.5s + 2.25s + 3.4s)
const retryWithBackoff = async (fn, maxRetries = 6, initialDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await fn();
      if (result.success) return result;
      if (result && !result.success) {
        throw new Error("Authentication failed");
      }
    } catch (error) {
      const isLastAttempt = attempt === maxRetries - 1;
      const is429 = error.response?.status === 429;

      // On last attempt, throw the error
      if (isLastAttempt) {
        throw error;
      }

      // Calculate delay based on error type
      // 404 = webhook might still be processing, wait for it
      // 429 = rate limited, wait longer
      const baseDelay = is429 ? initialDelay * 3 : initialDelay;
      const delay = baseDelay * Math.pow(1.5, attempt);
      console.log(
        `[Auth] Waiting for user creation... Retry ${attempt + 1}/${maxRetries} in ${Math.round(delay)}ms`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw new Error("Max retries exceeded");
};

const buildFallbackRegisterPayload = (clerkUser) => {
  const primaryEmail = clerkUser?.emailAddresses?.[0]?.emailAddress || null;

  return {
    clerkUserId: clerkUser?.id,
    email: primaryEmail,
    firstName: clerkUser?.firstName || "",
    lastName: clerkUser?.lastName || "",
    avatar: clerkUser?.imageUrl || null,
  };
};

// Sync status for UI feedback
export const SyncStatus = {
  IDLE: "idle",
  CONNECTING: "connecting",
  SYNCING: "syncing",
  SUCCESS: "success",
  ERROR: "error",
};

export const useAuthSync = (clerkUser, isSignedIn, isLoaded) => {
  const dispatch = useDispatch();
  const [authError, setAuthError] = useState(false);
  const [syncStatus, setSyncStatus] = useState(SyncStatus.IDLE);
  const [isNewUser, setIsNewUser] = useState(false);
  const syncInProgress = useRef(false);
  const reduxUser = useSelector((state) => state.loggedUser?.user);

  useEffect(() => {
    // Clear stale Redux user if Clerk says user is not signed in
    // This handles cases where redux-persist has old data
    if (isLoaded && !isSignedIn && reduxUser?.clerk_user_id) {
      console.log("[useAuthSync] Clearing stale Redux user - not signed in");
      dispatch(setCurrentUser(null));
      return;
    }

    // Handle stale state: clear Redux if Clerk user changed
    if (
      reduxUser?.clerk_user_id &&
      clerkUser?.id &&
      reduxUser.clerk_user_id !== clerkUser.id
    ) {
      console.warn("[useAuthSync] Stale state - clearing Redux user");
      dispatch(setCurrentUser(null));
      return;
    }

    // Skip sync if:
    // - Already syncing
    // - No Clerk user or not loaded
    // - Already have matching Redux user
    // - Signing out
    const alreadySynced = reduxUser?.clerk_user_id === clerkUser?.id;

    if (
      syncInProgress.current ||
      !clerkUser?.id ||
      !isLoaded ||
      !isSignedIn ||
      alreadySynced ||
      sessionStorage.getItem("signingOut") === "true"
    ) {
      return;
    }

    syncInProgress.current = true;
    setSyncStatus(SyncStatus.CONNECTING);

    // Determine if this is a new user (no existing Redux user with matching Clerk ID)
    // This helps show appropriate loading messages (e.g., "Creating account" vs "Signing in")
    const isReturningUser = !!reduxUser?.clerk_user_id;
    setIsNewUser(!isReturningUser);

    console.log(
      "[useAuthSync] Syncing user:",
      clerkUser.id,
      isReturningUser ? "(returning)" : "(new)",
    );

    const syncWithBackend = async () => {
      try {
        setSyncStatus(SyncStatus.SYNCING);
        const res = await retryWithBackoff(() =>
          authenticateUser(clerkUser.id),
        );

        if (!res.success) {
          console.error("[useAuthSync] Auth failed:", res);
          setAuthError(true);
          setSyncStatus(SyncStatus.ERROR);
          return;
        }

        // Store user in Redux - auth status will be derived from this
        dispatch(setCurrentUser(res.user));
        console.log("[useAuthSync] User synced:", res.user?.id);
        setSyncStatus(SyncStatus.SUCCESS);

        // If profile is complete, load settings
        if (isProfileComplete(res.user)) {
          const settingsRes = await getUserSettings(res.user.id);
          dispatch(
            settingsRes?.settings
              ? saveUserSettings(settingsRes.settings)
              : setDefaultSettings(),
          );
          markOnboardingComplete(res.user.id);
        }
      } catch (error) {
        const is404 = error.response?.status === 404;

        if (is404) {
          // Fallback: create the backend user directly if the webhook never produced a row.
          // This prevents fresh signups from getting trapped on a blank loading screen.
          try {
            console.warn(
              "[useAuthSync] User not found after retries. Creating fallback backend record.",
            );
            const fallbackRes = await registerUser(
              buildFallbackRegisterPayload(clerkUser),
            );

            if (fallbackRes?.success && fallbackRes.user) {
              dispatch(setCurrentUser(fallbackRes.user));
              setSyncStatus(SyncStatus.SUCCESS);

              if (isProfileComplete(fallbackRes.user)) {
                const settingsRes = await getUserSettings(fallbackRes.user.id);
                dispatch(
                  settingsRes?.settings
                    ? saveUserSettings(settingsRes.settings)
                    : setDefaultSettings(),
                );
                markOnboardingComplete(fallbackRes.user.id);
              }

              return;
            }

            console.error(
              "[useAuthSync] Fallback registration failed:",
              fallbackRes,
            );
          } catch (fallbackError) {
            console.error(
              "[useAuthSync] Fallback registration errored:",
              fallbackError,
            );
          }

          setAuthError(true);
          setSyncStatus(SyncStatus.ERROR);
        } else {
          console.error("[useAuthSync] Sync failed:", error);
          setAuthError(true);
          setSyncStatus(SyncStatus.ERROR);
        }
      } finally {
        syncInProgress.current = false;
      }
    };

    syncWithBackend();
  }, [
    clerkUser,
    clerkUser?.id,
    isLoaded,
    isSignedIn,
    reduxUser?.id,
    reduxUser?.clerk_user_id,
    dispatch,
  ]);

  return { authError, syncStatus, isNewUser };
};
